import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { motion } from "framer-motion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface MoodEntry {
  id: string;
  mood_level: number;
  journal_entry: string | null;
  created_at: string;
}

const MOODS = [
  { level: 1, emoji: "😢", label: "Very Low" },
  { level: 2, emoji: "😟", label: "Low" },
  { level: 3, emoji: "😐", label: "Neutral" },
  { level: 4, emoji: "🙂", label: "Good" },
  { level: 5, emoji: "😄", label: "Great" },
];

interface MoodIndicatorProps {
  onNoMoodLogged?: () => void;
}

export const MoodIndicator = ({ onNoMoodLogged }: MoodIndicatorProps) => {
  const [todayMood, setTodayMood] = useState<MoodEntry | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTodayMood = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const today = format(new Date(), "yyyy-MM-dd");
        
        const { data, error } = await supabase
          .from("mood_entries")
          .select("*")
          .eq("user_id", user.id)
          .gte("created_at", `${today}T00:00:00`)
          .lte("created_at", `${today}T23:59:59`)
          .order("created_at", { ascending: false })
          .limit(1);

        if (error) throw error;
        
        if (data && data.length > 0) {
          setTodayMood(data[0]);
        } else {
          onNoMoodLogged?.();
        }
      } catch (error) {
        console.error("Error fetching mood:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTodayMood();
  }, [onNoMoodLogged]);

  if (loading || !todayMood) return null;

  const mood = MOODS.find(m => m.level === todayMood.mood_level);
  if (!mood) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50 border border-border/50 cursor-default"
          >
            <span className="text-lg">{mood.emoji}</span>
            <span className="text-xs text-muted-foreground hidden sm:inline">Today</span>
          </motion.div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">Feeling {mood.label.toLowerCase()}</p>
          {todayMood.journal_entry && (
            <p className="text-xs text-muted-foreground mt-1 max-w-[200px] truncate">
              "{todayMood.journal_entry}"
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};